import { ChatSendPendingError } from '../../../services/modules/chatSendReceipt';
import { useCallback } from 'react';
import { ChatMessage } from './types';
import { chatRepository } from '../../../services/repositories/ChatRepository';
import { useMediaUpload } from './useMediaUpload';
import { createModuleLogger } from '../../../utils/logger';
import { notify } from '../../../utils/toast';
import { isDisplayableMediaUrl, isLocalMediaUrl } from '../../../utils/failedImageCache';
import {
    type EnqueueChatSend,
    awaitPersistSafe,
    markTempMessageFailed,
    reconcileTempMessageId,
} from '../lib/chatMessageMutations';

import { sendInstagramMediaMessage } from '../../../services/modules/instagramMessagingService';
import { sendWhatsappCloudMedia } from '../../../services/modules/whatsappCloudService';
import { uploadChatFileToR2 } from '../lib/uploadChatFile';

const log = createModuleLogger('[ChatMediaActions]');

interface UseChatMediaActionsProps {
    enqueueSend: EnqueueChatSend;
    instanceName: string | undefined;
    activeConversationId: string | null;
    chatPlatform?: 'whatsapp' | 'instagram';
    messagingConnectionId?: string | null;
    officialApi?: boolean;
    addOptimisticMessage: (msg: ChatMessage) => void;
    replaceOptimisticMessage: (oldId: string, newMsg: ChatMessage) => void;
    removeOptimisticMessage: (msgId: string) => void;
    ensureConversationExists: (text?: string) => Promise<string | null>;
    currentOrganizationId?: string;
    applySignature?: (text: string) => string;
}

export const useChatMediaActions = ({
    enqueueSend,
    instanceName,
    activeConversationId,
    chatPlatform = 'whatsapp',
    messagingConnectionId,
    officialApi = false,
    addOptimisticMessage,
    replaceOptimisticMessage,
    ensureConversationExists,
    currentOrganizationId,
    applySignature,
}: UseChatMediaActionsProps) => {

    const { getMediaType, uploadAndSend } = useMediaUpload(instanceName);

    const sendMedia = useCallback(async (file: File, caption?: string) => {
        log.log('📤 INICIANDO ENVIO DE MÍDIA');
        if (!activeConversationId) return;
        if (chatPlatform === 'whatsapp' && officialApi && !messagingConnectionId) return;
        if (chatPlatform === 'whatsapp' && !officialApi && !instanceName) return;
        if (chatPlatform === 'instagram' && !messagingConnectionId) return;

        const mediaType = officialApi && file.type.includes('webm') && getMediaType(file) === 'audio'
            ? 'document'
            : getMediaType(file);
        const captionText = caption?.trim() ? (applySignature ? applySignature(caption.trim()) : caption.trim()) : undefined;
        const captionLimit = chatPlatform === 'instagram' ? 1000 : officialApi ? 1024 : null;
        if (captionLimit && captionText && captionText.length > captionLimit) throw new Error(`Legenda excede ${captionLimit} caracteres, incluindo a assinatura.`);
        const requestId = crypto.randomUUID();
        const tempId = `temp-${requestId}`;
        const tempUrl = URL.createObjectURL(file);

        const optimisticMessage: ChatMessage = {
            id: tempId,
            type: mediaType,
            text: chatPlatform === 'instagram' ? undefined : captionText,
            sender: 'me',
            timestamp: new Date(),
            status: 'pending',
            media: {
                url: tempUrl,
                mimetype: file.type,
                filename: file.name
            }
        };

        addOptimisticMessage(optimisticMessage);

        let convId: string | null = null;
        const persistPromise = (async () => {
            const id = await ensureConversationExists();
            if (id) {
                await chatRepository.createMessage(id, {
                    ...optimisticMessage,
                    media: undefined,
                }, currentOrganizationId);
            }
            return id;
        })();
        void persistPromise.catch(() => undefined);

        return enqueueSend(async (onAccepted) => {
            try {
                convId = await persistPromise;
                if (!convId) throw new Error('Não foi possível salvar a conversa antes do envio.');
                let responseId = `sent-${Date.now()}`;
                let durableUrl: string | undefined;

                if (officialApi) {
                    const durableUpload = await uploadChatFileToR2(file);
                    const response = await sendWhatsappCloudMedia({
                        organizationId: currentOrganizationId!,
                        requestId, onAccepted,
                        integrationId: messagingConnectionId!,
                        to: activeConversationId,
                        kind: mediaType,
                        link: durableUpload,
                        caption: mediaType === 'audio' ? undefined : captionText,
                        filename: file.name,
                        mimeType: file.type,
                    });
                    responseId = response.messageId || responseId;
                    durableUrl = durableUpload;
                } else if (chatPlatform === 'instagram') {
                    const response = await sendInstagramMediaMessage({
                        organizationId: currentOrganizationId!,
                        requestId, onAccepted,
                        integrationId: messagingConnectionId!,
                        remoteJid: activeConversationId,
                        attachmentType: mediaType,
                        file,
                        caption: mediaType === 'audio' ? undefined : captionText,
                    });
                    responseId = response.messageId || responseId;
                    durableUrl = response.mediaUrl;
                    if (response.captionError) notify.error(String(response.captionError));
                } else {
                    const { url, messageId } = await uploadAndSend(file, activeConversationId, captionText, requestId, onAccepted);
                    responseId = messageId || responseId;
                    durableUrl =
                        url && isDisplayableMediaUrl(url) && !isLocalMediaUrl(url) ? url : undefined;
                }

                replaceOptimisticMessage(tempId, {
                    id: responseId,
                    type: mediaType,
                    text: chatPlatform === 'instagram' ? undefined : captionText,
                    sender: 'me',
                    timestamp: optimisticMessage.timestamp,
                    status: 'sent',
                    media: {
                        url: durableUrl || tempUrl,
                        mimetype: file.type,
                        filename: file.name,
                    },
                });

                convId = await persistPromise;
                await reconcileTempMessageId({
                    conversationId: convId,
                    tempId,
                    realId: responseId,
                    patch: {
                        message_type: mediaType,
                        status: 'sent',
                        ...(durableUrl ? { media_url: durableUrl } : {}),
                    },
                    peerNotify: currentOrganizationId
                        ? {
                            organizationId: currentOrganizationId,
                            content: captionText,
                            messageType: mediaType,
                            messageTimestamp: optimisticMessage.timestamp,
                        }
                        : undefined,
                });

                // Revoga depois do paint: revoke síncrono dispara onError no <img> ainda com blob.
                if (durableUrl) {
                    const toRevoke = tempUrl;
                    window.setTimeout(() => URL.revokeObjectURL(toRevoke), 30_000);
                }

                log.log('✅ Mídia enviada com sucesso!');

            } catch (error: any) {
                log.error('❌ Erro ao enviar mídia:', error);

                replaceOptimisticMessage(tempId, {
                    ...optimisticMessage,
                    status: error instanceof ChatSendPendingError ? 'uncertain' : 'failed',
                });
                convId = convId ?? (await awaitPersistSafe(persistPromise));
                await markTempMessageFailed(tempId, convId, error instanceof ChatSendPendingError ? 'uncertain' : 'failed');

                if (error instanceof ChatSendPendingError) {
                    notify.error(error.message);
                    throw error;
                }
                const is503 = String(error?.message || '').includes('503');
                if (error?.message?.includes('muito grande') || error?.message?.includes('suportados')) {
                    notify.info(error.message);
                } else if (is503) {
                    notify.error('WhatsApp indisponível no momento. Tente novamente em alguns segundos.');
                } else if (officialApi || chatPlatform === 'instagram') {
                    const msg = String(error?.message || '');
                    if (/unauthorized|não autenticado|faça login/i.test(msg)) {
                        notify.error('Sessão expirada. Faça login novamente.');
                    } else {
                        notify.error(msg || (officialApi
                            ? 'Falha ao enviar mídia no WhatsApp. Tente novamente.'
                            : 'Falha ao enviar mídia no Instagram. Tente novamente.'));
                    }
                } else {
                    notify.error('Falha ao enviar arquivo. A mídia ficou marcada como erro — tente novamente.');
                }
                throw error;
            }
        });
    }, [enqueueSend, instanceName, activeConversationId, chatPlatform, messagingConnectionId, officialApi, getMediaType, uploadAndSend, ensureConversationExists, currentOrganizationId, addOptimisticMessage, replaceOptimisticMessage, applySignature]);

    return { sendMedia };
};
